import { of, EMPTY, from } from 'rxjs';
import moment from 'moment';
import { combineEpics, ofType } from 'redux-observable';
import { map, switchMap, mergeMap, expand, reduce } from 'rxjs/operators';

import { rs } from 'core/api';
import { TepIntents } from 'constants/tep';
import { reduxSwitch } from 'core/redux/tools';
import { actions as devicesActions } from 'core/redux/api/devices';
import { internalizePage, timestamps } from 'core/redux/serializers';
import { ActionGeneratorBuilder, errorMap } from 'core/redux/actions';
import { exportTepManagement } from 'helpers/export/exportTepManagement';

const defaultPageSize = 10;

const internalize = timestamps(['createdAt', 'seenAt', 'sinceAt']).internalize;

export const actions = new ActionGeneratorBuilder('veta-entry-passes-leases')
	.subtype('load', (load) => load.request('payload').success({ page: true }).fail().cancel())
	.subtype('update', (update) => update.request({ id: true, data: true }).success({ id: true, data: true }).fail())
	.subtype('updateMultiple', (updateMultiple) => updateMultiple.request('data').success().fail())
	.subtype('draft', (draft) => draft.request({ data: true }).success().fail())
	.subtype('issue', (issue) => issue.request('data').success().fail())
	.subtype('undraft', (undraft) => undraft.request({ id: true }).success().fail())
	.subtype('undraftMultiple', (undraftMultiple) => undraftMultiple.request({ ids: true }).success().fail())
	.subtype('remove', (remove) => remove.request({ id: true, on: true }).success().fail())
	.subtype('removeMultiple', (removeMultiple) => removeMultiple.request('data').success().fail())
	.subtype('export', (exp) => exp.request('payload').success().fail())
	.type('intent', { intent: true })
	.type('select', { leases: true })
	.type('edit', { id: true })
	.type('setTableInstance', { table: true })
	.type('setMarker', { marker: true })
	.type('close')
	.type('clear')
	.build();

const defaultState = {
	intent: null,
	selectedId: null,
	selected: [],
	data: null,
	marker: null,
	params: { no: 1, search: undefined, size: 10, sort: undefined },
	request: { isLoading: false, error: null },
	export: { isLoading: false, error: null },
};

const setLeases = (state, action) => {
	const { intent, selectedId, marker } = state;
	const isMarkerPreserved = !!marker && action.page?.objects?.find((pass) => pass.id === marker.id);

	const leases = {
		data: action.page,
		marker: isMarkerPreserved ? marker : null,
		request: { isLoading: false, error: null },
	};

	if ((intent === TepIntents.InlineEdit || intent === TepIntents.InlineUndraft) && selectedId) {
		return { ...state, selectedId: null, intent: null, ...leases };
	}

	if (intent === TepIntents.Issue && selectedId) {
		return { ...state, selectedId: null, ...leases };
	}

	if (intent) {
		return { ...state, intent: null, selected: [], ...leases };
	}

	return { ...state, ...leases };
};

const updateLease = (state, action) => {
	if (state.intent !== TepIntents.Issue) {
		return state;
	}

	return {
		...state,
		selected: state.selected.map((entryPass) =>
			entryPass.id === action.id ? { ...entryPass, lease: { ...entryPass.lease, ...action.data } } : entryPass,
		),
	};
};

const leasesReducer = (state, action) => {
	switch (action.type) {
		case actions.load.request.type:
			return { ...state, request: { isLoading: true, error: null }, params: action.payload };
		case actions.update.request.type:
		case actions.updateMultiple.request.type:
		case actions.draft.request.type:
		case actions.issue.request.type:
		case actions.undraft.request.type:
		case actions.undraftMultiple.request.type:
		case actions.remove.request.type:
		case actions.removeMultiple.request.type:
			return { ...state, request: { isLoading: true, error: null } };
		case actions.export.request.type:
			return { ...state, export: { isLoading: true, error: null } };
		case actions.load.success.type:
			return setLeases(state, action);
		case actions.update.success.type:
			return updateLease(state, action);
		case actions.export.success.type:
			return { ...state, export: { isLoading: false, error: null } };
		case actions.load.fail.type:
		case actions.update.fail.type:
		case actions.updateMultiple.fail.type:
		case actions.draft.fail.type:
		case actions.issue.fail.type:
		case actions.undraft.fail.type:
		case actions.undraftMultiple.fail.type:
		case actions.remove.fail.type:
		case actions.removeMultiple.fail.type:
			return { ...state, request: { isLoading: false, error: action.errorMessage } };
		case actions.export.fail.type:
			return { ...state, export: { isExporting: false, error: action.errorMessage } };
		case actions.intent.type:
			return { ...state, intent: action.intent };
		case actions.select.type:
			return { ...state, selected: action.leases };
		case actions.edit.type:
			return { ...state, selectedId: action.id };
		case actions.setTableInstance.type:
			return { ...state, table: action.table };
		case actions.setMarker.type:
			return { ...state, marker: action.marker };
		case actions.close.type:
			return { ...state, intent: null, selected: [], selectedId: null, request: { isLoading: false, error: null } };
		case actions.clear.type:
			return { ...state, request: { isLoading: false, error: null } };
		default:
			return state;
	}
};

export const reducer = reduxSwitch([leasesReducer], defaultState);

const loadEpic = (action$) => {
	return action$.pipe(
		ofType(actions.load.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/page?${action.payload.filters}`)
				.optional('no', action.payload.no)
				.optional('sort', action.payload.sort)
				.optional('search', action.payload.search)
				.param('size', action.payload.size | defaultPageSize)
				.defer$()
				.pipe(
					map(internalizePage(action.payload.size | defaultPageSize, internalize)),
					map((page) => actions.load.success({ page })),
					errorMap(actions.load.fail),
				),
		),
	);
};

const updateEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.update.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/${action.id}/lease/draft`)
				.put(action.data)
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							actions.update.success({ id: action.id, data: action.data }),
							actions.load.request({ payload: state$.value.veta.tep.leases.params }),
						),
					),
					errorMap(actions.update.fail),
				),
		),
	);
};

const updateMultipleEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.updateMultiple.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/lease/draft/batch`)
				.put(action.data)
				.defer$()
				.pipe(
					map(() => actions.load.request({ payload: state$.value.veta.tep.leases.params })),
					errorMap(actions.update.fail),
				),
		),
	);
};

const draftEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.draft.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/lease/draft/batch`)
				.post(action.data)
				.defer$()
				.pipe(
					map(() => actions.load.request({ payload: state$.value.veta.tep.leases.params })),
					errorMap(actions.draft.fail),
				),
		),
	);
};

const issueEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.issue.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/lease/issue/batch`)
				.param('on', moment(new Date()).format('YYYY-MM-DD'))
				.post(action.data)
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							devicesActions.load.request(),
							actions.load.request({ payload: state$.value.veta.tep.leases.params }),
						),
					),
					errorMap(actions.issue.fail),
				),
		),
	);
};

const undraftEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.undraft.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/${action.id}/lease/draft`)
				.delete()
				.defer$()
				.pipe(
					map(() => actions.load.request({ payload: state$.value.veta.tep.leases.params })),
					errorMap(actions.undraft.fail),
				),
		),
	);
};

const undraftMultipleEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.undraftMultiple.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/lease/draft/batch?${action.ids.map((id) => `id=${id}`).join('&')}`)
				.delete()
				.defer$()
				.pipe(
					map(() => actions.load.request({ payload: state$.value.veta.tep.leases.params })),
					errorMap(actions.undraftMultiple.fail),
				),
		),
	);
};

const removeEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.remove.request.type),
		switchMap((action) =>
			rs(`veta/entry-passes/${action.id}/lease`)
				.param('on', action.on)
				.delete()
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							devicesActions.load.request(),
							actions.load.request({ payload: state$.value.veta.tep.leases.params }),
						),
					),
					errorMap(actions.remove.fail),
				),
		),
	);
};

const removeMultipleEpic = (action$, state$) => {
	return action$.pipe(
		ofType(actions.removeMultiple.request.type),
		switchMap((action) =>
			rs('veta/entry-passes/lease/return/batch')
				.post(action.data)
				.defer$()
				.pipe(
					mergeMap(() =>
						of(
							devicesActions.load.request(),
							actions.load.request({ payload: state$.value.veta.tep.leases.params }),
						),
					),
					errorMap(actions.removeMultiple.fail),
				),
		),
	);
};

const exportEpic = (action$) => {
	return action$.pipe(
		ofType(actions.export.request.type),
		switchMap((action) => {
			const pageSize = 100;
			const initialParams = {
				no: 1,
				size: pageSize,
				sort: action.payload?.sort,
				search: action.payload?.search,
				filters: action.payload?.filters,
			};

			const loadPage = (params) =>
				rs(`veta/entry-passes/page?${params.filters}`)
					.optional('no', params.no)
					.optional('sort', params.sort)
					.optional('search', params.search)
					.param('size', params.size)
					.defer$()
					.pipe(map(internalizePage(params.size, internalize)));

			return loadPage(initialParams).pipe(
				expand((page) => {
					if (page.no >= Math.ceil(page.total / pageSize)) {
						return EMPTY;
					}
					return loadPage({ ...initialParams, no: page.no + 1 });
				}),
				reduce((acc, page) => {
					return [...acc, ...page.objects];
				}, []),
				switchMap((allData) => {
					return from(exportTepManagement(action.payload.format, allData)).pipe(
						map(() => actions.export.success())
					);
				}),
				errorMap(actions.export.fail),
			);
		}),
	);
};

export const epic = combineEpics(
	loadEpic,
	updateEpic,
	updateMultipleEpic,
	draftEpic,
	issueEpic,
	undraftEpic,
	undraftMultipleEpic,
	removeEpic,
	removeMultipleEpic,
	exportEpic,
);
